import { AddIcon, ArrowDropDownIcon, Fab, FabLink, makeStyles, Theme, Typography } from '@freighthub/silhouette-core'
import cn from 'classnames'
import { FC, MouseEvent, useContext, useState } from 'react'
import { authorize } from 'service/roles'
import { useAccess } from 'service/useAccess'
import { useI18n } from 'utils/localisation'

import { NewQuoteButtonContext } from './NewQuoteButtonContext'
import { NewQuoteMenu } from './NewQuoteMenu'
import { NewQuotePopup } from './NewQuotePopup'

export const testIds = {
  link: 'new-quote-button-link',
  anchor: 'new-quote-button-anchor',
  menu: 'new-quote-button-menu',
  popup: 'new-quote-button-popup',
}

type StyleProps = { grouped: boolean }

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    display: 'inline-flex',
    '& > *:not(:last-child)': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    '& > *:not(:first-child)': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  fab: {
    height: theme.spacing(5),
    width: theme.spacing(5),
    boxShadow: `0 1px 3px ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
    '&&': {
      padding: theme.spacing(0, 1.5),
    },
    '&:hover': {
      zIndex: theme.zIndex.drawer + 1,
      boxShadow: `0 2px 10px ${theme.palette.grey[500]}`,
      backgroundColor: theme.palette.background.paper,
    },
    '&:not(:first-child)': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    '&:not(:last-child)': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  fabExtended: {
    justifyContent: 'flex-start',
    width: 'auto',
  },
  fabText: ({ grouped }) => ({
    marginTop: 0,
    marginBottom: 0,
    marginLeft: theme.spacing(1),
    marginRight: grouped ? theme.spacing(1) : theme.spacing(1.5),
  }),
}))

export interface NewQuoteButtonProps {
  expanded: boolean
  className?: string
}

export const NewQuoteButton: FC<NewQuoteButtonProps> = ({ expanded, className, ...props }) => {
  const hasAccess = useAccess({ roles: authorize.membersOrBilling })
  const { translate } = useI18n()
  const classes = useStyles({ grouped: hasAccess })
  const [anchorEl, setAnchorEl] = useState(null)
  const { reload } = useContext(NewQuoteButtonContext)

  const handleClickAnchor = (event: MouseEvent<HTMLButtonElement>) => {
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    reload() // To get the latest quotes and templates in case there is a change
    // @ts-expect-error TS(2345) FIXME: Argument of type '(EventTarget & HTMLButtonElement... Remove this comment to see the full error message
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }
  const handleClose = () => setAnchorEl(null)

  // Supplier cannot request quotes and can only book via booking templates
  if (!hasAccess) {
    return (
      <div className={cn(classes.root, className)} {...props}>
        <FabLink
          to="/booking-templates"
          variant={expanded ? 'extended' : 'circular'}
          classes={{ root: classes.fab, extended: classes.fabExtended }}
          onClick={handleClose}
        >
          <AddIcon className={classes.addIcon} />
          {expanded && (
            <Typography variant="caption" color="textPrimary" className={classes.fabText}>
              {translate('header-request_quote_only_from_template')}
            </Typography>
          )}
        </FabLink>
      </div>
    )
  }

  return (
    <>
      <div className={cn(classes.root, className)} data-userlane-id="button-new-quote" {...props}>
        {expanded && (
          <FabLink
            to="/quote-request"
            variant="extended"
            classes={{ root: classes.fab, extended: classes.fabExtended }}
            onClick={handleClose}
            data-testid={testIds.link}
            data-e2e-id="quote-request-header"
            data-userlane-id="button-new-quote-main"
            sx={{ textTransform: 'none' }}
          >
            <AddIcon className={classes.addIcon} />
            <Typography variant="caption" color="textPrimary" className={classes.fabText}>
              {translate('sidebar-new_quote')}
            </Typography>
          </FabLink>
        )}
        <Fab
          className={classes.fab}
          onClick={handleClickAnchor}
          data-testid={testIds.anchor}
          data-userlane-id={expanded ? 'button-new-quote-more' : undefined}
        >
          {expanded ? <ArrowDropDownIcon fontSize="x-small" /> : <AddIcon className={classes.addIcon} />}
        </Fab>
      </div>
      {expanded ? (
        <NewQuotePopup open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose} data-testid={testIds.popup} />
      ) : (
        <NewQuoteMenu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose} data-testid={testIds.menu} />
      )}
    </>
  )
}
